import React from 'react'
import { FormikProps, useFormikContext } from 'formik'

import clsx from 'clsx'
import {
  BoxV4,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtonsV2,
} from '@labrav/react-components'

import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-regular-svg-icons'

import { Product } from '../../../../modules/productsListing/reducer'
import { useStyles } from './RenewalSectionCombinedForm.styles'
import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { HelpDrawer } from '@labrav/common'
export const renewalOptions: RadioButtonGroupMapping[] = [
  {
    key: 'Yes',
    label: 'Yes',
    type: RadioButtonType.STANDARD,
  },
  {
    key: 'No',
    label: 'No',
    type: RadioButtonType.STANDARD,
  },
]

export type RenewalSectionCombinedFormProps = {
  product?: Product
  disabled: boolean
}
export const RenewalSectionCombinedForm: React.FC<
  RenewalSectionCombinedFormProps
> = ({ disabled }) => {
  const { values, setFieldValue, errors, touched, setFieldTouched } =
    useFormikContext() as FormikProps<CombinedFormState>
  // error
  const renewalError = errors.resellerAgreementData?.renewal
  const renewalChannelError = errors.resellerAgreementData?.renewalChannel
  const renewalPricingModelError =
    errors.resellerAgreementData?.renewalPricingModel
  //touched
  const renewalTouched = touched.resellerAgreementData?.renewal
  const renewalChannelTouched = touched.resellerAgreementData?.renewalChannel
  const renewalPricingModelTouched =
    touched.resellerAgreementData?.renewalPricingModel

  //is error check
  const hasRenewalError = renewalError && renewalTouched
  const hasRenewalChannelError = renewalChannelError && renewalChannelTouched
  const hasRenewalPricingError =
    renewalPricingModelError && renewalPricingModelTouched
  const { classes } = useStyles()
  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    if (value === 'Yes') {
      await setFieldValue('resellerAgreementData.renewal', true)
      await setFieldValue('resellerAgreementData.renewalChannel', 'EXTERNAL')
    } else if (value === 'No') {
      await setFieldValue('resellerAgreementData.renewal', false)
      await setFieldValue('resellerAgreementData.renewalChannel', null)
      await setFieldValue('resellerAgreementData.renewalPricingModel', null)
    } else {
      await setFieldValue('resellerAgreementData.renewal', '')
      await setFieldValue('resellerAgreementData.renewalChannel', null)
      await setFieldValue('resellerAgreementData.renewalPricingModel', null)
    }
    setFieldTouched('resellerAgreementData.renewal', true)
  }
  const handleSelectionRenewalType = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    await setFieldTouched('resellerAgreementData.renewalChannel', true)
    await setFieldValue('resellerAgreementData.renewalChannel', value)
  }

  const handleSelectionPricingModalType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value

    setFieldTouched('resellerAgreementData.renewalPricingModel', true)
    setFieldValue('resellerAgreementData.renewalPricingModel', value)
  }
  const checkedValue = () =>
    values.resellerAgreementData.renewal
      ? 'Yes'
      : values.resellerAgreementData.renewal === false
        ? 'No'
        : ''
  const channelTypes: RadioButtonGroupMapping[] = [
    {
      key: 'EXTERNAL',
      label: 'Existing customer moving to AWS Marketplace',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'An existing customer you have been doing business with, and the next deal is migrating to AWS Marketplace as the transaction vehicle.',
      disabled: disabled,
    },
    {
      key: 'AWS_MARKETPLACE',
      label: 'Existing customer on AWS Marketplace',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Buyers are getting an offer to renew an existing agreement created on AWS Marketplace.',
      disabled: disabled,
    },
  ]
  const PricingModal: RadioButtonGroupMapping[] = [
    {
      key: 'Contract',
      label: 'Contract',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer entered into a contract with you entitling them to a specified quantity of use for an agreed-upon length of time',
      disabled: disabled,
    },
    {
      key: 'Usage',
      label: 'Usage',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was billed for their usage of your product based on the per-unit price you set.',
      disabled: disabled,
    },

    {
      key: 'Byol',
      label: 'Bring your own license',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was entitled to usage of your product by supplying a valid license key purchased outside of AWS Marketplace.',
      disabled: disabled,
    },
    {
      key: 'Free',
      label: 'Free',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was entitled to usage of your product at no charge, either through a free trial or a $0 subscription.',
      disabled: disabled,
    },
  ]
  return (
    <div data-testid="renewal-section">
      <BoxV4
        heading={'Renewal'}
        headingIcon={
          <FontAwesomeIcon
            icon={faRepeat}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName="combinedFormRenewal"
            headerTitle="Help & FAQs"
          />
        }
      >
        <div className={classes.sectionContainer}>
          <Typography
            className={clsx(classes.sectionText, classes.renewalChannelMragin)}
          >
            Is this offer intended to renew an existing paid subscription with
            an existing customer for the same underlying product?
          </Typography>
          <div className={classes.gapError}>
            <div className={classes.widthRadio}>
              <RadioButtonsV2
                checkedValue={{
                  key: checkedValue(),
                  value: checkedValue(),
                }}
                name={'resellerAgreementData.renewal'}
                handleChange={handleSelection}
                radioButtonMappings={renewalOptions.map(option => ({
                  ...option,
                  disabled: disabled,
                }))}
              />
            </div>
            {hasRenewalError && (
              <Typography className={classes.partnerTypeError}>
                {renewalError}
              </Typography>
            )}
          </div>
        </div>
        {values.resellerAgreementData.renewal && (
          <div data-testid="renewal-type" className={classes.sectionContainer}>
            <Typography
              className={clsx(
                classes.sectionHeading,
                classes.renewalChannelMragin
              )}
            >
              Renewal type
            </Typography>
            <div className={classes.gapError}>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: 'channel',
                    value: values.resellerAgreementData.renewalChannel || '',
                  }}
                  name={'resellerAgreementData.renewalChannel'}
                  handleChange={handleSelectionRenewalType}
                  radioButtonMappings={channelTypes}
                  vertical
                />
              </div>
              {hasRenewalChannelError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalChannelError}
                </Typography>
              )}
            </div>
          </div>
        )}
        {values.resellerAgreementData.renewal && (
          <div className={classes.sectionContainer}>
            <Typography
              className={clsx(
                classes.sectionHeading,
                classes.renewalChannelMragin
              )}
            >
              Previous agreement pricing model
            </Typography>
            <div className={classes.gapError}>
              <div className={classes.radioWithRadio}>
                <RadioButtonsV2
                  checkedValue={{
                    key: 'channel',
                    value:
                      values.resellerAgreementData.renewalPricingModel || '',
                  }}
                  name={'resellerAgreementData.renewalPricingModel'}
                  handleChange={handleSelectionPricingModalType}
                  radioButtonMappings={PricingModal}
                />
              </div>
              {hasRenewalPricingError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalPricingModelError}
                </Typography>
              )}
            </div>
          </div>
        )}
      </BoxV4>
    </div>
  )
}
